/**
 * Neo4j-style force-directed knowledge graph.
 * - Uses refs for all state to avoid stale closures
 * - Native DOM events on window for reliable drag
 * - Directional edges (DAG arrows) with relationship name only
 * - Clean nodes, no buttons
 */

import React, { useRef, useEffect, useState } from 'react';
import {
  forceSimulation, forceLink, forceManyBody, forceCenter,
  forceCollide, forceX, forceY,
} from 'd3-force';
import { colorTheme } from '../../theme/colors';

const mkDC = (c) => ({
  fill: c.main, stroke: c.dark, text: '#fff',
  region: `${c.main}10`, label: `${c.main}50`,
});

const DOMAIN_COLORS = {
  core:      mkDC(colorTheme.brand),       // #f58025
  finance:   mkDC(colorTheme.teal),        // #009391
  marketing: mkDC(colorTheme.pink),        // #ed145b
  sales:     mkDC(colorTheme.green),       // #b0bc22
  analytics: mkDC(colorTheme.brightblue),  // #0093cf
};
const DEFAULT_DC = mkDC(colorTheme.darkblue);

// Brand colors used for selection/highlight states.
const ACCENT       = colorTheme.brand.main;   // #f58025
const ACCENT_DARK  = colorTheme.brand.dark;   // #e66610
const ACCENT_TINT  = `${colorTheme.brand.main}1A`; // ~10% alpha bg for selected label
const EDGE_GREY    = colorTheme.grey.dark;    // #a5abc9 — default edge color
const LABEL_BORDER = colorTheme.grey.main;    // #dcdeef
const LABEL_TEXT   = colorTheme.darkblue.main; // #152451

export default function ForceGraph({
  schemas, relationships, onNodeClick, onEdgeClick,
  onDoubleClickNode, selectedNode, selectedEdgeIdx, hiddenNodes, width, height,
}) {
  const canvasRef = useRef(null);
  const simRef = useRef(null);
  const nodesRef = useRef([]);
  const edgesRef = useRef([]);
  const dragRef = useRef(null);
  const hoverRef = useRef(null);
  const trRef = useRef({ x: 0, y: 0, k: 1 });
  const [, setTick] = useState(0);
  const rerender = () => setTick(t => t + 1);

  // Refs for props so event handlers always see latest
  const selectedNodeRef = useRef(selectedNode);
  const selectedEdgeRef = useRef(selectedEdgeIdx);
  selectedNodeRef.current = selectedNode;
  selectedEdgeRef.current = selectedEdgeIdx;

  const hiddenSet = useRef(new Set());
  hiddenSet.current = new Set(hiddenNodes || []);

  // Screen → graph coords
  function s2g(sx, sy) {
    const r = canvasRef.current.getBoundingClientRect();
    const t = trRef.current;
    return { x: (sx - r.left - t.x) / t.k, y: (sy - r.top - t.y) / t.k };
  }

  // Hit test against current nodes/edges
  function hitTest(sx, sy) {
    const { x, y } = s2g(sx, sy);
    for (const n of nodesRef.current) {
      if ((x - n.x) ** 2 + (y - n.y) ** 2 < n.radius ** 2) return { type: 'node', node: n };
    }
    for (const e of edgesRef.current) {
      if (e._mx != null && (x - e._mx) ** 2 + (y - e._my) ** 2 < 400) return { type: 'edge', edge: e };
    }
    return null;
  }

  // Build simulation
  useEffect(() => {
    if (!schemas.length) return;
    const hs = hiddenSet.current;
    const visible = schemas.filter(s => !hs.has(s.table));
    const visIds = new Set(visible.map(s => s.table));

    const oldPos = {};
    for (const n of nodesRef.current) oldPos[n.id] = { x: n.x, y: n.y };

    const nodes = visible.map(s => ({
      id: s.table,
      entity: s.entity || s.table.replace(/^canonical[._]/, ''),
      domain: s.domain,
      description: s.description || '',
      fieldCount: s.field_count || (s.fields || []).length,
      x: oldPos[s.table]?.x ?? width / 2 + (Math.random() - 0.5) * 300,
      y: oldPos[s.table]?.y ?? height / 2 + (Math.random() - 0.5) * 300,
      radius: Math.max(30, 20 + (s.field_count || 0) * 1.2),
    }));

    const pc = {};
    const rawEdges = relationships
      .filter(r => visIds.has(r.from_table) && visIds.has(r.to_table))
      .map((r, i) => {
        const pk = [r.from_table, r.to_table].sort().join('::');
        pc[pk] = (pc[pk] || 0) + 1;
        return {
          idx: i, source: r.from_table, target: r.to_table,
          label: r.label || r.from_field, type: r.type || 'fk',
          join_key: r.from_field || r.join_key, note: r.note, pairKey: pk,
        };
      });
    const pi = {};
    for (const e of rawEdges) { pi[e.pairKey] = pi[e.pairKey] || 0; e.pairIndex = pi[e.pairKey]++; e.pairTotal = pc[e.pairKey]; }

    nodesRef.current = nodes;
    edgesRef.current = rawEdges;

    const seen = new Set();
    const fLinks = rawEdges.filter(e => { if (seen.has(e.pairKey)) return false; seen.add(e.pairKey); return true; })
      .map(e => ({ source: e.source, target: e.target }));

    // Domain centroids — spaced around the canvas so each cluster gets
    // its own region. Tighter than the upstream defaults so the canonical
    // domains read as visually distinct groups by default.
    const dc = {
      core:      { x: width * 0.50, y: height * 0.50 }, // center — most fanout
      finance:   { x: width * 0.20, y: height * 0.30 },
      marketing: { x: width * 0.80, y: height * 0.30 },
      sales:     { x: width * 0.80, y: height * 0.75 },
      analytics: { x: width * 0.20, y: height * 0.75 },
    };

    if (simRef.current) simRef.current.stop();
    simRef.current = forceSimulation(nodes)
      // Looser links so cross-domain ties don't drag clusters together.
      .force('link', forceLink(fLinks).id(d => d.id).distance(220).strength(0.1))
      // Lower repulsion → tighter intra-cluster packing.
      .force('charge', forceManyBody().strength(-320).distanceMax(380))
      // Drop the global center force; per-domain centers handle layout.
      .force('collision', forceCollide().radius(d => d.radius + 14))
      // Strong pull toward the domain centroid — this is the grouping force.
      .force('cx', forceX(d => (dc[d.domain] || { x: width / 2 }).x).strength(0.22))
      .force('cy', forceY(d => (dc[d.domain] || { y: height / 2 }).y).strength(0.22))
      .velocityDecay(0.6)
      .alphaDecay(0.04)
      .on('tick', rerender);

    return () => simRef.current?.stop();
    // eslint-disable-next-line
  }, [schemas, relationships, width, height, hiddenNodes]);

  // Render canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr; canvas.height = height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const t = trRef.current;

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#F8FAFC'; ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#EEF2F6'; ctx.lineWidth = 0.5;
    const gs = 40 * t.k;
    for (let x = t.x % gs; x < width; x += gs) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke(); }
    for (let y = t.y % gs; y < height; y += gs) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke(); }

    ctx.save(); ctx.translate(t.x, t.y); ctx.scale(t.k, t.k);
    const nodes = nodesRef.current;
    const edges = edgesRef.current;
    const selNode = selectedNodeRef.current;
    const selEdge = selectedEdgeRef.current;

    // Domain regions
    const groups = {};
    for (const n of nodes) (groups[n.domain] = groups[n.domain] || []).push(n);
    for (const [dom, grp] of Object.entries(groups)) {
      const c = DOMAIN_COLORS[dom] || DEFAULT_DC;
      const cx = grp.reduce((s, n) => s + n.x, 0) / grp.length;
      const cy = grp.reduce((s, n) => s + n.y, 0) / grp.length;
      let mr = 0;
      for (const n of grp) { const d = Math.sqrt((n.x - cx) ** 2 + (n.y - cy) ** 2) + n.radius; if (d > mr) mr = d; }
      ctx.beginPath(); ctx.arc(cx, cy, mr + 50, 0, Math.PI * 2);
      ctx.fillStyle = c.region; ctx.fill();
      ctx.strokeStyle = c.label; ctx.lineWidth = 1 / t.k;
      ctx.setLineDash([5 / t.k, 4 / t.k]); ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle = c.label; ctx.font = `600 ${11 / t.k}px -apple-system,sans-serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'top';
      ctx.fillText(dom.toUpperCase(), cx, cy - mr - 50 + 6 / t.k);
    }

    // Edges — directional with arrow and label
    for (const edge of edges) {
      const sn = typeof edge.source === 'object' ? edge.source : nodes.find(n => n.id === edge.source);
      const tn = typeof edge.target === 'object' ? edge.target : nodes.find(n => n.id === edge.target);
      if (!sn || !tn) continue;
      const isSel = selEdge === edge.idx;

      const dx = tn.x - sn.x, dy = tn.y - sn.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const spread = 28;
      const off = edge.pairTotal > 1 ? (edge.pairIndex - (edge.pairTotal - 1) / 2) * spread : 0;
      const nx = -dy / dist, ny = dx / dist;
      const mx = (sn.x + tn.x) / 2 + nx * off;
      const my = (sn.y + tn.y) / 2 + ny * off;

      // Line
      ctx.beginPath(); ctx.moveTo(sn.x, sn.y); ctx.quadraticCurveTo(mx, my, tn.x, tn.y);
      ctx.strokeStyle = isSel ? ACCENT : EDGE_GREY;
      ctx.lineWidth = (isSel ? 2.5 : 1.2) / t.k;
      ctx.stroke();

      // Arrowhead at target edge
      const ang = Math.atan2(tn.y - my, tn.x - mx);
      const al = 10 / t.k;
      const ax = tn.x - Math.cos(ang) * tn.radius;
      const ay = tn.y - Math.sin(ang) * tn.radius;
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(ax - al * Math.cos(ang - 0.25), ay - al * Math.sin(ang - 0.25));
      ctx.lineTo(ax - al * Math.cos(ang + 0.25), ay - al * Math.sin(ang + 0.25));
      ctx.closePath();
      ctx.fillStyle = isSel ? ACCENT : EDGE_GREY; ctx.fill();

      // Label — just the relationship name, nothing else
      if (t.k > 0.35 && edge.label) {
        const fs = 9 / t.k;
        ctx.font = `500 ${fs}px -apple-system,sans-serif`;
        const tw = ctx.measureText(edge.label).width;
        const pad = 5 / t.k;

        ctx.fillStyle = isSel ? ACCENT_TINT : 'rgba(255,255,255,0.92)';
        ctx.strokeStyle = isSel ? ACCENT : LABEL_BORDER;
        ctx.lineWidth = 0.6 / t.k;
        ctx.beginPath();
        ctx.roundRect(mx - tw / 2 - pad, my - fs / 2 - pad * 0.6, tw + pad * 2, fs + pad * 1.2, 3 / t.k);
        ctx.fill(); ctx.stroke();

        ctx.fillStyle = isSel ? ACCENT_DARK : LABEL_TEXT;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(edge.label, mx, my);
      }

      edge._mx = mx; edge._my = my;
    }

    // Nodes
    for (const node of nodes) {
      const c = DOMAIN_COLORS[node.domain] || DEFAULT_DC;
      const isSel = selNode === node.id;

      if (isSel) {
        ctx.beginPath(); ctx.arc(node.x, node.y, node.radius + 5, 0, Math.PI * 2);
        ctx.fillStyle = `${c.fill}20`; ctx.fill();
        ctx.strokeStyle = c.fill; ctx.lineWidth = 2 / t.k; ctx.stroke();
      }

      ctx.beginPath(); ctx.arc(node.x + 1 / t.k, node.y + 1 / t.k, node.radius, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0,0,0,0.04)'; ctx.fill();

      ctx.beginPath(); ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
      const g = ctx.createRadialGradient(node.x - node.radius * 0.3, node.y - node.radius * 0.3, 0, node.x, node.y, node.radius);
      g.addColorStop(0, c.fill); g.addColorStop(1, c.stroke);
      ctx.fillStyle = g; ctx.fill();
      ctx.strokeStyle = isSel ? colorTheme.darkblue.main : 'rgba(255,255,255,0.2)';
      ctx.lineWidth = (isSel ? 2.5 : 0.8) / t.k; ctx.stroke();

      ctx.fillStyle = c.text;
      ctx.font = `700 ${12 / t.k}px -apple-system,sans-serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(node.entity, node.x, node.y - 2 / t.k);
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = `${9 / t.k}px -apple-system,sans-serif`;
      ctx.fillText(`${node.fieldCount} fields`, node.x, node.y + 10 / t.k);
    }

    // Hover tooltip
    const hNode = hoverRef.current;
    if (hNode && hNode.description && !dragRef.current) {
      const fs = 10 / t.k;
      const maxW = 200 / t.k;
      ctx.font = `400 ${fs}px -apple-system,sans-serif`;

      // Word wrap
      const words = hNode.description.split(' ');
      const lines = [];
      let line = '';
      for (const w of words) {
        const test = line ? line + ' ' + w : w;
        if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = w; }
        else line = test;
      }
      if (line) lines.push(line);

      const pad = 8 / t.k;
      const lineH = fs * 1.4;
      const boxW = Math.min(maxW + pad * 2, lines.reduce((m, l) => Math.max(m, ctx.measureText(l).width), 0) + pad * 2);
      const boxH = lines.length * lineH + pad * 2;
      const bx = hNode.x - boxW / 2;
      const by = hNode.y + hNode.radius + 8 / t.k;

      ctx.fillStyle = 'rgba(30,41,59,0.92)';
      ctx.beginPath(); ctx.roundRect(bx, by, boxW, boxH, 4 / t.k); ctx.fill();

      // Arrow
      ctx.beginPath();
      ctx.moveTo(hNode.x - 5 / t.k, by);
      ctx.lineTo(hNode.x, by - 4 / t.k);
      ctx.lineTo(hNode.x + 5 / t.k, by);
      ctx.fill();

      ctx.fillStyle = '#E2E8F0';
      ctx.textAlign = 'left'; ctx.textBaseline = 'top';
      for (let i = 0; i < lines.length; i++) {
        ctx.fillText(lines[i], bx + pad, by + pad + i * lineH);
      }
    }

    ctx.restore();
  });

  // Event handlers — use plain functions that read refs (no stale closures)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    function onDown(e) {
      const hit = hitTest(e.clientX, e.clientY);
      if (hit?.type === 'node') {
        const gp = s2g(e.clientX, e.clientY);
        dragRef.current = { type: 'node', node: hit.node, ox: gp.x - hit.node.x, oy: gp.y - hit.node.y, sx: e.clientX, sy: e.clientY, moved: false };
        hit.node.fx = hit.node.x;
        hit.node.fy = hit.node.y;
        simRef.current?.alphaTarget(0.08).restart();
      } else {
        dragRef.current = { type: 'pan', sx: e.clientX, sy: e.clientY, tx: trRef.current.x, ty: trRef.current.y, moved: false };
      }
      e.preventDefault();
    }

    function onMove(e) {
      const d = dragRef.current;
      if (!d) {
        const hit = hitTest(e.clientX, e.clientY);
        canvas.style.cursor = hit ? 'pointer' : 'default';
        const newHover = hit?.type === 'node' ? hit.node : null;
        if (hoverRef.current !== newHover) { hoverRef.current = newHover; rerender(); }
        return;
      }
      hoverRef.current = null;
      const mx = e.clientX - d.sx, my = e.clientY - d.sy;
      if (Math.abs(mx) > 2 || Math.abs(my) > 2) d.moved = true;

      if (d.type === 'pan') {
        trRef.current.x = d.tx + mx;
        trRef.current.y = d.ty + my;
        rerender();
      } else if (d.type === 'node') {
        const gp = s2g(e.clientX, e.clientY);
        d.node.fx = gp.x - d.ox;
        d.node.fy = gp.y - d.oy;
        canvas.style.cursor = 'grabbing';
      }
    }

    function onUp(e) {
      const d = dragRef.current;
      if (!d) return;
      if (d.type === 'node') {
        if (d.moved) {
          // Leave pinned where dropped
        } else {
          d.node.fx = null; d.node.fy = null;
          onNodeClick?.(d.node.id);
        }
        simRef.current?.alphaTarget(0);
      } else if (d.type === 'pan' && !d.moved) {
        const hit = hitTest(e.clientX, e.clientY);
        if (hit?.type === 'edge') onEdgeClick?.(hit.edge.idx);
        else { onNodeClick?.(null); onEdgeClick?.(null); }
      }
      dragRef.current = null;
      canvas.style.cursor = 'default';
    }

    function onDbl(e) {
      const hit = hitTest(e.clientX, e.clientY);
      if (hit?.type === 'node') {
        hit.node.fx = null; hit.node.fy = null;
        simRef.current?.alpha(0.3).restart();
        onDoubleClickNode?.(hit.node.id);
      }
    }

    function onWhl(e) {
      e.preventDefault();
      const t = trRef.current;
      const r = canvas.getBoundingClientRect();
      const cx = e.clientX - r.left, cy = e.clientY - r.top;
      const f = e.deltaY > 0 ? 0.93 : 1.07;
      const nk = Math.max(0.2, Math.min(3, t.k * f));
      t.x = cx - (cx - t.x) * (nk / t.k);
      t.y = cy - (cy - t.y) * (nk / t.k);
      t.k = nk;
      rerender();
    }

    canvas.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    canvas.addEventListener('dblclick', onDbl);
    canvas.addEventListener('wheel', onWhl, { passive: false });

    return () => {
      canvas.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      canvas.removeEventListener('dblclick', onDbl);
      canvas.removeEventListener('wheel', onWhl);
    };
    // Re-attach when callbacks change
  }, [onNodeClick, onEdgeClick, onDoubleClickNode]);

  return <canvas ref={canvasRef} style={{ width, height, display: 'block' }} />;
}
